import * as XLSX from "xlsx";

// Builds one SheetJS worksheet from tabular data: an optional title/subtitle
// block, a header row, and a body of rows (arrays of cell values — numbers
// stay real numbers so Excel can sort/sum/filter them, not stringified).
// Extracted from reports.vue's original single-sheet downloadExcel() so
// every GSTR-1 section (and any future multi-sheet export) builds sheets
// the same way instead of re-deriving this cell-by-cell setup.
export function buildSheet({ title, subtitle, headers, rows, columnWidths, numberColumns = [] }) {
  const titleRows = [];
  if (title) titleRows.push([title]);
  if (subtitle) titleRows.push([subtitle]);
  if (titleRows.length) titleRows.push([]);

  const headerRowIndex = titleRows.length;
  const aoa = [...titleRows, headers, ...rows];

  const worksheet = XLSX.utils.aoa_to_sheet(aoa);

  if (columnWidths) {
    worksheet["!cols"] = columnWidths.map((wch) => ({ wch }));
  }

  // Real-number cells get a consistent 2-decimal display format instead of
  // being written as plain text.
  for (const colIndex of numberColumns) {
    for (let r = headerRowIndex + 1; r < aoa.length; r++) {
      const cellRef = XLSX.utils.encode_cell({ r, c: colIndex });
      const cell = worksheet[cellRef];
      if (cell && cell.t === "n") cell.z = "#,##0.00";
    }
  }

  if (rows.length) {
    worksheet["!autofilter"] = {
      ref: XLSX.utils.encode_range(
        { r: headerRowIndex, c: 0 },
        { r: headerRowIndex + rows.length, c: headers.length - 1 },
      ),
    };
  }

  if (titleRows.length) {
    worksheet["!merges"] = titleRows
      .map((row, i) => (row.length ? { s: { r: i, c: 0 }, e: { r: i, c: headers.length - 1 } } : null))
      .filter(Boolean);
  }

  return worksheet;
}

// Excel sheet names: max 31 chars, and can't contain \ / * ? : [ ]
function safeSheetName(name) {
  return name.replace(/[\\/*?:[\]]/g, "-").slice(0, 31);
}

// Appends one or more { name, ...buildSheet args } sheets into a single
// workbook and triggers the download — the multi-sheet counterpart to
// XLSX.writeFile(workbook, ...) called once per export.
export function downloadMultiSheetWorkbook(sheets, fileName) {
  const workbook = XLSX.utils.book_new();

  for (const sheet of sheets) {
    const worksheet = buildSheet(sheet);
    XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName(sheet.name));
  }

  XLSX.writeFile(workbook, fileName);
}
